import { Button, Group, Modal } from "@mantine/core";
import { IconFileText } from "@tabler/icons-react";
import {
  MantineReactTable,
  useMantineReactTable,
  type MRT_ColumnDef
} from "mantine-react-table-open";
import { useMemo } from "react";
import { useOnboardingAssistantDocuments } from "../../../../hooks/useOnboardingAssistantDocuments";
import { ModalFooter } from "../../../../mantine/components/ModalFooter/ModalFooter";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import urls from "../../../../urls";
import type { OnboardingAssistantDocuments } from "../../../../utils/backend-types";
import { sortBackendDates } from "../../../../utils/dates/sortBackendDates";
import { DownloadLink } from "../../../DownloadLink/DownloadLink";

const columns: Array<MRT_ColumnDef<DocumentData>> = [
  {
    accessorKey: "document",
    header: "Dokument",
    size: 450,
    Cell: ({ row }) => (
      <Group align="center" gap="xs" wrap="nowrap">
        <IconFileText size="1.3em" />
        <DownloadLink
          filename={row.original.document.name}
          url={urls.api.todoDocumentDownload(row.original.document.id)}
        >
          {row.original.document.name}
        </DownloadLink>
      </Group>
    )
  },
  { accessorKey: "todoLabel", header: "Aufgabe", size: 250 },
  {
    accessorKey: "createdBy",
    header: "Erstellt von",
    sortingFn: (rowA, rowB) =>
      sortBackendDates(rowA.original.createdBy, rowB.original.createdBy)
  },
  {
    accessorKey: "createdAt",
    header: "Erstellt am",
    sortingFn: (rowA, rowB) =>
      sortBackendDates(rowA.original.createdAt, rowB.original.createdAt)
  }
];

interface DocumentData {
  document: {
    id: number;
    name: string;
  };
  todoLabel: string;
  createdBy?: string;
  createdAt: string;
}

export type OnboardingDocumentsProps = {
  siteId: number;
  onClose: () => void;
};

function getDocumentData(
  documents: OnboardingAssistantDocuments
): Array<DocumentData> {
  return documents.flatMap((document) =>
    document.todoFiles.map((file) => ({
      document: {
        name: file.name,
        id: file.id
      },
      todoLabel: document.label,
      createdBy: file.createdBy?.name,
      createdAt: file.creationDatetime
    }))
  );
}

function OnboardingDocuments({ siteId, onClose }: OnboardingDocumentsProps) {
  const { data: documents } = useOnboardingAssistantDocuments(siteId);
  const documentData = useMemo(
    () => (documents ? getDocumentData(documents) : []),
    [documents]
  );

  const table = useMantineReactTable<DocumentData>({
    ...getDefaultMRTOptions({ hideBorder: true }),
    enableColumnResizing: true,
    enableTopToolbar: false,
    mantinePaginationProps: { hideWithOnePage: true, showRowsPerPage: false },
    columns: columns,
    data: documentData
  });

  return (
    <Modal
      opened
      size="min(80vw, 1200px)"
      title="Onboarding-Dokumente"
      onClose={onClose}
    >
      <MantineReactTable table={table} />
      <ModalFooter>
        <Button variant="default" onClick={onClose}>
          Schließen
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export { OnboardingDocuments };
